/**
 * Règles de période d'une page : préréglages, bornes, période par défaut.
 *
 * Toutes les pages partagent les mêmes règles (cf. `lib/dates`), sauf les pages
 * de campagne, cadrées sur leurs dates : BF 26 n'existe que du 1er novembre au
 * 31 décembre 2026, s'ouvre sur hier et se parcourt jour par jour.
 */

import { BF26_PLAN } from './bf26';
import {
  PERIOD_PRESETS,
  dataScopeForPath,
  latestDataDay,
  presetRange,
  presetsForScope,
  shiftDay,
  todayInParis,
  type DataScope,
  type PeriodPreset,
} from './dates';

export interface PeriodBounds {
  /** Premier jour sélectionnable. */
  min: string;
  /** Dernier jour sélectionnable pour la fin de période. */
  max: string;
  /** Dernier jour sélectionnable pour le début : il faut au moins un jour de données. */
  maxStart: string;
}

export interface PeriodRules {
  scope: DataScope;
  presets: readonly PeriodPreset[];
  /** `null` : pas de bornes propres à la page. */
  bounds: PeriodBounds | null;
  /** Boutons jour précédent / suivant. */
  dayStepper: boolean;
  defaultPeriod: () => { from: string; to: string; presetId: string | null };
}

const lower = (a: string, b: string) => (a < b ? a : b);
const higher = (a: string, b: string) => (a > b ? a : b);
export const clampDay = (day: string, min: string, max: string) => higher(min, lower(day, max));

const BF26_FROM = BF26_PLAN[0]?.from ?? '2026-11-01';
const BF26_TO = BF26_PLAN[BF26_PLAN.length - 1]?.to ?? '2026-12-31';

function bf26Bounds(scope: DataScope): PeriodBounds {
  const maxStart = higher(BF26_FROM, lower(BF26_TO, latestDataDay(scope)));
  // La fin va jusqu'au 31 déc. : on pilote la campagne entière, les jours à venir sont rabattus par l'API.
  return { min: BF26_FROM, max: BF26_TO, maxStart };
}

function bf26Rules(scope: DataScope): PeriodRules {
  const bounds = () => bf26Bounds(scope);
  const day = (iso: string) => {
    const d = clampDay(iso, bounds().min, bounds().maxStart);
    return { from: d, to: d };
  };
  const presets: PeriodPreset[] = [];
  // « Aujourd'hui » et « Hier » gardent leurs identifiants : ils suivent d'une page à l'autre.
  if (todayInParis() >= BF26_FROM && todayInParis() <= BF26_TO) {
    presets.push({ id: 'today', label: "Aujourd'hui", range: () => day(todayInParis()) });
  }
  const yesterday = shiftDay(todayInParis(), -1);
  if (yesterday >= BF26_FROM && yesterday <= BF26_TO) {
    presets.push({ id: 'yesterday', label: 'Hier', range: () => day(yesterday) });
  }
  presets.push({ id: 'bf26', label: 'Toute la campagne', range: () => ({ from: BF26_FROM, to: bounds().max }) });
  return {
    scope,
    presets,
    bounds: bounds(),
    dayStepper: true,
    defaultPeriod: () => ({ ...day(yesterday), presetId: presets.some((p) => p.id === 'yesterday') ? 'yesterday' : null }),
  };
}

// Mois en cours par défaut : du 1er du mois au dernier jour de données.
const DEFAULT_PRESET_ID = 'mtd';

export function periodRulesForPath(pathname: string): PeriodRules {
  const scope = dataScopeForPath(pathname);
  if (pathname === '/bf26') return bf26Rules(scope);
  return {
    scope,
    presets: presetsForScope(scope),
    bounds: null,
    dayStepper: false,
    defaultPeriod: () => {
      const preset = PERIOD_PRESETS.find((p) => p.id === DEFAULT_PRESET_ID) ?? PERIOD_PRESETS[0];
      return { ...(preset ? preset.range(scope) : presetRange(30, scope)), presetId: preset?.id ?? DEFAULT_PRESET_ID };
    },
  };
}
